type StoryblokFallbackProps = {
  component?: string;
};

function LoadingStatus({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="sr-only"
      role="status"
    >
      {children}
    </span>
  );
}

export function PageHeroFallback() {
  return (
    <section
      aria-busy="true"
      className="relative flex min-h-[78vh] w-full items-end overflow-hidden bg-accent sm:min-h-screen"
    >
      <LoadingStatus>Paginaheader wordt geladen</LoadingStatus>

      <div className="absolute inset-0 bg-gradient-to-br from-primary via-accent to-primary/80" />
      <div className="absolute inset-0 animate-pulse bg-gradient-to-t from-black/35 via-transparent to-white/5 motion-reduce:animate-none" />

      <div className="container relative z-10 pb-12 pt-40 md:pb-18 md:pt-48">
        <div className="max-w-3xl animate-pulse space-y-5 motion-reduce:animate-none">
          <div className="h-3 w-28 rounded-full bg-white/28" />
          <div className="h-12 w-full max-w-2xl rounded-full bg-white/24 sm:h-16" />
          <div className="h-12 w-3/5 max-w-md rounded-full bg-white/18 sm:h-16" />
          <div className="pt-2">
            <div className="h-4 w-full max-w-xl rounded-full bg-white/18" />
            <div className="mt-3 h-4 w-4/5 max-w-lg rounded-full bg-white/14" />
          </div>
        </div>
      </div>
    </section>
  );
}

export function PageContentFallback() {
  return (
    <section
      aria-busy="true"
      className="w-full bg-muted py-16 sm:py-24 lg:py-32"
    >
      <LoadingStatus>Pagina-inhoud wordt geladen</LoadingStatus>

      <div className="container">
        <div className="grid animate-pulse items-center gap-12 motion-reduce:animate-none lg:grid-cols-2 lg:gap-16">
          <div className="space-y-5">
            <div className="h-3 w-24 rounded-full bg-secondary/25" />
            <div className="h-10 w-4/5 rounded-full bg-primary/15 sm:h-12" />
            <div className="space-y-3 pt-2">
              <div className="h-4 w-full rounded-full bg-primary/10" />
              <div className="h-4 w-11/12 rounded-full bg-primary/10" />
              <div className="h-4 w-3/4 rounded-full bg-primary/10" />
            </div>
          </div>

          <div className="aspect-[4/3] w-full bg-primary/10 shadow-soft" />
        </div>
      </div>
    </section>
  );
}

export function StoryblokSectionFallback({ component }: StoryblokFallbackProps) {
  if (component === 'HeroMedia') {
    return <PageHeroFallback />;
  }

  return <PageContentFallback />;
}

export function StoryblokBlockFallback({ component }: StoryblokFallbackProps) {
  if (component === 'Button' || component === 'Link') {
    return (
      <span
        aria-hidden="true"
        className="inline-flex h-11 w-36 animate-pulse rounded-full bg-primary/12 motion-reduce:animate-none"
      />
    );
  }

  if (
    component === 'FormCheckboxField' ||
    component === 'FormDatePickerField' ||
    component === 'FormHiddenField' ||
    component === 'FormNumberField' ||
    component === 'FormPhoneField' ||
    component === 'FormSelectField' ||
    component === 'FormTextField' ||
    component === 'FormTextareaField'
  ) {
    return (
      <span
        aria-hidden="true"
        className="block h-12 w-full animate-pulse rounded-sm bg-primary/10 motion-reduce:animate-none"
      />
    );
  }

  if (component === 'ExternalVideo' || component === 'Image' || component === 'InlineImage') {
    return (
      <span
        aria-hidden="true"
        className="block aspect-video w-full animate-pulse bg-primary/10 motion-reduce:animate-none"
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className="block min-h-24 w-full animate-pulse bg-primary/8 motion-reduce:animate-none"
    />
  );
}
